import Scrollspy from 'react-scrollspy-ez';
import styled from 'styled-components';

import BadgeStyled from '../../../components/Badge';
import ContentHeaderStyled from '../../../components/ContentHeader';
import ExternalLinkGroupStyled from '../../../components/ExternalLinkGroup';
import PaginationStyled from '../../../components/Pagination';
import { REFERENCE_LINKS } from '../../../constants';
import { IconDanger, IconSuccess } from '../../../core';
import { TypographyStyle } from '../../../design-tokens';
import commonPageContentStyles, {
  textStyle,
} from '../../../styles/mixins/_commonPageContentStyles';
import navigationStyles from '../../../styles/mixins/navigationMenuStyles';

import { describeStyle, fontSizeRows, styleLabels, typeScaleStyles } from './typeScale';

interface Props {
  className?: string;
}

// The four Do / Don't rows of Color Misuse (13752:140442, :140451, :140460, :140469); the captures are the frame's cards at 2x.
const colorMisuseRows = [
  {
    id: 'orange-heading',
    doAlt: 'A heading in DHCS Black on a light gray card',
    dontAlt: 'A heading in DHCS Orange on a light gray card, struck through',
    guidance:
      'Do not use DHCS Orange on any text. Headings use DHCS Black, as shown in the heading guidelines.',
  },
  {
    id: 'orange-emphasis',
    doAlt: 'A heading in DHCS Black on a light gray card',
    dontAlt: 'A heading in DHCS Orange on a light gray card, struck through',
    guidance:
      'Do not use DHCS Orange for emphasis or on links within text. Use DHCS Teal, DHCS Blue, or Black to emphasize body copy; for links, use DHCS Teal.',
  },
  {
    id: 'accent-gray',
    doAlt: 'A heading in DHCS Black on a light gray card',
    dontAlt: 'A light gray card whose Accent Gray text cannot be read, struck through',
    guidance:
      'Do not use Accent Gray for text on light backgrounds. Only use Accent Gray on dark backgrounds such as DHCS Blue or black.',
  },
  {
    id: 'teal-on-blue',
    doAlt: 'A heading in white on a DHCS Blue card',
    dontAlt: 'A heading in DHCS Teal on a DHCS Blue card, struck through',
    guidance:
      'Do not use DHCS Teal text on DHCS Blue backgrounds. Use white text on DHCS Blue or DHCS Teal backgrounds instead.',
  },
];

const dataExternalLinkGroups = [
  {
    id: 1,
    title: 'Government usability testing resources',
    cards: [
      {
        title: 'IBM Web Accessibility Checklist',
        subtitle: '[ibm.com]',
        linkUrl: REFERENCE_LINKS.ibmAccessibilityChecklist,
      },
      {
        title: 'Government usability testing resources',
        subtitle: '[digital.gov]',
        linkUrl: REFERENCE_LINKS.digitalGovUsabilityTesting,
      },
    ],
  },
  {
    id: 2,
    title: 'Government accessibility resources',
    cards: [
      {
        title: 'Learn about writing in plain language ',
        subtitle: '[digital.gov]',
        linkUrl: REFERENCE_LINKS.digitalGovPlainLanguageGuide,
      },
      {
        title: 'The Section 508 accessibility program',
        subtitle: '[section508.gov]',
        linkUrl: REFERENCE_LINKS.section508Program,
      },
      {
        title: 'Accessibility for Teams guide',
        subtitle: '[digital.gov]',
        linkUrl: REFERENCE_LINKS.digitalGovAccessibilityForTeams,
      },
      {
        title: 'Find your Section 508 Coordinator',
        subtitle: '[section508.gov]',
        linkUrl: REFERENCE_LINKS.section508ProgramManagerListing,
      },
      {
        title: 'Section 508 ICT Testing Baseline',
        subtitle: '[ictbaseline.access-board.gov]',
        linkUrl: REFERENCE_LINKS.ictTestingBaseline,
      },
    ],
  },
];

// A piece of text set in one extracted style; the style reaches it as var() strings through the theme.
const Sample = styled.span<{ $style: TypographyStyle }>`
  font-family: ${({ theme, $style }) => theme.typography[$style].fontFamily}, sans-serif;
  ${({ $style }) => textStyle($style)}
  ${({ $style }) => $style === 'bodyOverline' && 'text-transform: uppercase;'}
`;

const IMAGES = '/images/branding-styleguide-typography';

const TypographyStructure = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="content">
      <ContentHeaderStyled
        title="Typography"
        breadcrumbs={[
          { label: 'Branding Styleguide', url: '/branding-styleguide' },
          { label: 'Typography' },
        ]}
      />
      <div className="page-container">
        <div className="page-content">
          <div className="page-content-with-navigation-wrapper">
            <div className="page-content-with-navigation">
              <div className="page-content-item">
                <h2 className="h1" id="overview">
                  Overview
                </h2>
                <h3 className="h3" id="definition">
                  Definition
                </h3>
                <p className="mb-40">
                  Typography on the DHCS design system is more than visual design—it’s a functional
                  tool that shapes how users interact with content. By using carefully selected
                  typefaces, weights, and sizes, we create a clear hierarchy that enhances
                  readability and guides users seamlessly through their experience. Typography
                  reflects the portal’s personality, ensuring every piece of information is both
                  accessible and visually engaging, reinforcing trust and clarity in every
                  interaction.
                </p>
              </div>
              <img
                src={`${IMAGES}/typography-definition@2x.webp`}
                alt="Public Sans specimen: the letters Aa, the name Public Sans, and the Bold and Regular weights"
                width={1000}
                height={340}
                className="mb-40 img-responsive"
              />
              <div className="number-cards mb-40">
                <div className="number-card">
                  <div className="number-card-img">
                    <img
                      src={`${IMAGES}/screen-heading@2x.webp`}
                      alt="Heading example: Hello world, set as Heading 1"
                      width={488}
                      height={320}
                    />
                  </div>
                  <div className="number-card-desc">
                    <strong>Heading</strong>
                    <p>Heading is used to create various levels of typographic hierarchies.</p>
                  </div>
                </div>
                <div className="number-card">
                  <div className="number-card-img">
                    <img
                      src={`${IMAGES}/screen-body@2x.webp`}
                      alt="Body example: Single Streamlined Application, set as Body 1 B"
                      width={488}
                      height={320}
                    />
                  </div>
                  <div className="number-card-desc">
                    <strong>Body</strong>
                    <p>Body is primarily used for Spectrum components and for blocks of text.</p>
                  </div>
                </div>
                <div className="number-card">
                  <div className="number-card-img">
                    <img
                      src={`${IMAGES}/screen-actions@2x.webp`}
                      alt="Actions example: an Our recommendations button above the text Example of Detail XL"
                      width={488}
                      height={320}
                    />
                  </div>
                  <div className="number-card-desc">
                    <strong>Actions</strong>
                    <p>
                      Detail is used for disclosing extra information or smaller items in
                      hierarchical relationships of text.
                    </p>
                  </div>
                </div>
              </div>
              <h2 className="divider h1" id="specifications">
                Specifications
              </h2>
              <div className="page-content-item">
                <h3 className="h3" id="anatomy">
                  Anatomy
                </h3>
                <p className="mb-40">
                  The DHCS design system utilizes the modern and versatile typeface Public Sans, an
                  open-source font meticulously crafted to embody a spirit of clarity,
                  functionality, and innovation. Designed to support diverse global audiences,
                  Public Sans reflects a harmonious balance between technology and humanity. This
                  typeface forms the backbone of our typographic identity, ensuring every word
                  communicates with purpose and precision. For accessibility and flexibility, Public
                  Sans can be downloaded directly from the official GitHub repository, ensuring
                  seamless integration into any project.
                </p>
                <h3 className="h3" id="font-sizes">
                  Font sizes
                </h3>
                <p className="mb-40">
                  Font sizes in the DHCS design system follow a structured and scalable typographic
                  system designed for clarity and accessibility. Built on the Public Sans type
                  scale, this system is guided by a single equation that ensures a harmonious
                  hierarchy across all user experiences. The formula, starting with a base size of
                  y₀=12px, provides consistent scaling for headings, body text, and supporting
                  details. This approach ensures optimal readability while seamlessly adapting to
                  various screen sizes and resolutions, delivering a balanced and responsive
                  interface.
                </p>
              </div>
              <table className="table table-colors table-font mb-40">
                <thead>
                  <tr>
                    <th>Titles</th>
                    <th>Font Family</th>
                    <th>Weight</th>
                    <th>Size</th>
                    <th>Line height</th>
                    <th>Letter spacing</th>
                  </tr>
                </thead>
                <tbody>
                  {fontSizeRows.map(({ style, label }) => {
                    const values = describeStyle(style);

                    return (
                      <tr key={style}>
                        <td>
                          <Sample $style={style}>{label}</Sample>
                        </td>
                        <td>{values.family}</td>
                        <td>{values.weightTitle}</td>
                        <td>{values.size}</td>
                        <td>{values.lineHeight}</td>
                        <td>{values.tracking}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <h2 className="divider h1" id="guidelines">
                Guidelines
              </h2>
              <div className="page-content-item">
                <h3 className="h3" id="best-practices">
                  Best practices
                </h3>
                <p className="mb-40">
                  Typography is a cornerstone of design, shaping how users perceive and interact
                  with content. It creates a clear visual hierarchy that directs attention and
                  improves comprehension. Thoughtful application of font sizes, weights, and styles
                  reinforces this structure, ensuring consistency and readability across devices.
                  Limiting font families and adhering to capitalization rules further simplifies
                  content, allowing key messages to stand out. When used effectively, typography not
                  only conveys information but also enhances the overall user experience, making it
                  intuitive and engaging.
                </p>
              </div>
              <div className="ex-cards">
                <div className="ex-card true">
                  <img
                    className="ex-card-img"
                    src="/images/branding-styleguide-typography/screen-best-practices-01@2x.webp"
                    alt=""
                    width={488}
                    height={409}
                  />
                  <div className="ex-card-desc">
                    <BadgeStyled className="success" iconLeft={<IconSuccess />}>
                      Do
                    </BadgeStyled>
                    <p>
                      Respect capitalization rules. Use sentence case for all UX content. For more
                      on capitalization guidelines, go to Grammar and mechanics.
                    </p>
                  </div>
                </div>
                <div className="ex-card false">
                  <img
                    src="/images/branding-styleguide-typography/screen-best-practices-02@2x.webp"
                    alt=""
                    width={488}
                    height={409}
                  />
                  <div className="ex-card-desc">
                    <BadgeStyled className="danger" iconLeft={<IconDanger />}>
                      Don&rsquo;t
                    </BadgeStyled>
                    <p>
                      Don&apos;t use underlines for adding emphasise. Underlines are reserved for
                      text links only. They should not be used as a way for adding emphasis to
                      words.
                    </p>
                  </div>
                </div>
              </div>
              <div className="page-content-item">
                <h3 className="h3" id="line-length">
                  Line length
                </h3>
                <p className="mb-40">
                  Line length plays a crucial role in the readability and flow of content. A good
                  goal is to aim for between 60 and 100 characters per line, including spacing. This
                  range strikes a balance between readability and flow, ensuring that users can
                  easily follow the text without feeling overwhelmed or losing their place. Lines
                  that are too short can disrupt the reading experience, while those that are too
                  long may cause eye strain or make it harder to follow the content. Maintaining an
                  optimal line length ensures a seamless, comfortable reading experience, improving
                  user engagement and content comprehension across the platform.
                </p>
                <p className="mb-40">
                  Setting an optimal line length will break up content into easily digestible
                  information.
                </p>
              </div>
              <figure>
                <img
                  src={`${IMAGES}/screen-line-length-01@2x.webp`}
                  alt="Line length diagram: the same text at four measures, marked at 0, 30, 40, 60 and 90 characters, with 40 to 60 highlighted as ideal"
                  width={1000}
                  height={681}
                  className="mb-40 img-responsive"
                />
                <figcaption>
                  The ideal line length is 40-60 characters per line for English body text.
                </figcaption>
              </figure>
              <figure>
                <img
                  src={`${IMAGES}/screen-line-length-02@2x.webp`}
                  alt="Line length diagram for short lines: the same text at three measures, marked at 0, 20, 40 and 50 characters, with 20 to 40 highlighted as ideal"
                  width={1000}
                  height={430}
                  className="img-responsive"
                />
                <figcaption>
                  The ideal line length for short lines of English text is 20-40 characters per
                  line.
                </figcaption>
              </figure>
              <h2 className="divider h1" id="specifications-for-design-system">
                Specifications for Design System
              </h2>
              <div className="page-content-item">
                <h3 className="h3" id="anatomy-design-system">
                  Anatomy
                </h3>
                <p>
                  The typeface chosen for DHCS design system is designed with versatility in mind,
                  supporting multiple languages and character sets to cater to our diverse
                  international audience. Its thoughtful construction offers various weights and
                  styles, making it adaptable for different contexts, from digital interfaces to
                  print materials. This flexibility ensures that the typeface can maintain clarity
                  and legibility across all user interactions.
                </p>
                <p>
                  Being open-source, the typeface allows for ongoing customization and improvement,
                  fostering contributions from the design community to enhance its functionality and
                  expand its potential. This collaborative approach ensures that the typeface
                  continues to meet evolving needs and standards.
                </p>
                <p className="mb-40">
                  The typeface can be accessed and downloaded from the DHCS GitHub Repo.
                </p>
              </div>
              <table className="table table-colors table-font-2 mb-40">
                <thead>
                  <tr>
                    <th colSpan={6} aria-label="hh" />
                  </tr>
                </thead>
                <tbody>
                  {typeScaleStyles.map((style) => {
                    const values = describeStyle(style);

                    return (
                      <tr key={style}>
                        <td>
                          <Sample $style={style} data-type-style={style}>
                            {styleLabels[style]}
                          </Sample>
                        </td>
                        <td>{values.family}</td>
                        <td>{values.weight}</td>
                        <td>{values.size}</td>
                        <td>{values.lineHeight}</td>
                        <td>{values.tracking}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="page-content-item">
                <h3 className="h3" id="color-misuse">
                  Color Misuse
                </h3>
                <p>
                  The bright colors must be used carefully to ensure proper contrast and readability
                  at all times. Refer to the guidance below for what to avoid when applying text
                  colors. For the easiest way to comply with contrast requirements, follow the
                  heading guidelines or use an approved DHCS template. Note: all of these rules
                  still apply if the background and text colors are switched.
                </p>
              </div>
              <div className="color-misuse mb-40">
                <div className="color-misuse-row color-misuse-header">
                  <span className="do">Do This</span>
                  <span className="dont">Don&rsquo;t Do This</span>
                  <span>Guidance</span>
                </div>
                {colorMisuseRows.map((row) => (
                  <div className="color-misuse-row" key={row.id}>
                    <img
                      src={`/images/branding-styleguide-typography/color-misuse-${row.id}-do@2x.webp`}
                      alt={row.doAlt}
                      width={240}
                      height={88}
                    />
                    <img
                      src={`/images/branding-styleguide-typography/color-misuse-${row.id}-dont@2x.webp`}
                      alt={row.dontAlt}
                      width={240}
                      height={88}
                    />
                    <p>{row.guidance}</p>
                  </div>
                ))}
              </div>
              <h2 className="h1 divider" id="references">
                References
              </h2>
              {dataExternalLinkGroups.map((externalLinkGroup) => (
                <ExternalLinkGroupStyled
                  key={externalLinkGroup.id}
                  title={externalLinkGroup.title}
                  cards={externalLinkGroup.cards}
                />
              ))}
            </div>
            <div>
              <div className="page-navigation">
                <h2>Contents</h2>
                <Scrollspy
                  ids={[
                    'overview',
                    'definition',
                    'specifications',
                    'anatomy',
                    'font-sizes',
                    'guidelines',
                    'best-practices',
                    'line-length',
                    'specifications-for-design-system',
                    'anatomy-design-system',
                    'color-misuse',
                    'references',
                  ]}
                  offset={0}
                  activeItemClassName="active"
                  itemContainerClassName="multilevel-link-list"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <PaginationStyled
        previousLink="/branding-styleguide/colors"
        previous="Colors"
        nextLink="/branding-styleguide/logos"
        next="Logos"
      />
    </div>
  </div>
);

const Typography = styled(TypographyStructure)`
  ${commonPageContentStyles}
  ${navigationStyles([2, 4, 5, 7, 8, 11])}

    overflow: unset;
  height: 100%;

  > .content {
    width: 100%;
    padding: 0;
  }

  .number-cards {
    strong {
      ${textStyle('h4Sb')}
      color: ${({ theme }) => theme.neutralTextWeak};
    }

    p {
      ${textStyle('paragraphMedium')}
      color: ${({ theme }) => theme.neutralTextWeak};
    }
  }

  /* The Updated frame sets the two line length captions in Neutral/text-disabled: H4, then Paragraph/Large. */
  figure figcaption {
    ${textStyle('h4')}
    color: ${({ theme }) => theme.neutralTextDisabled};
  }

  figure + figure figcaption {
    ${textStyle('paragraphLarge')}
  }

  /* Color Misuse (13752:140434): the two card captures are 240px, the guidance takes the rest, rows 14px apart. */
  .color-misuse {
    display: flex;
    flex-direction: column;
    gap: 0.875rem;
  }

  /* Below md the two 240px captures leave no width for the guidance, so it takes its own row. */
  .color-misuse-row {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 15rem));
    gap: 1rem;
    align-items: start;

    > :nth-child(3) {
      grid-column: 1 / -1;
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
      grid-template-columns: 15rem 15rem minmax(0, 1fr);

      > :nth-child(3) {
        grid-column: auto;
      }
    }

    img {
      display: block;
      width: 100%;
      height: auto;
    }

    p {
      ${textStyle('body14')}
      margin: 0;
      color: ${({ theme }) => theme.neutralTextWeak};
    }
  }

  .color-misuse-header span {
    ${textStyle('h4Sb')}
    color: ${({ theme }) => theme.neutralTextWeak};

    &.do {
      color: ${({ theme }) => theme.successText};
    }

    &.dont {
      color: ${({ theme }) => theme.alertText};
    }
  }

  .table {
    &-font,
    &-font-2 {
      table-layout: fixed;

      tr:last-child {
        border-bottom: 1px solid ${({ theme }) => theme.neutralBorder};
      }

      td {
        height: 4.375rem;
        color: ${({ theme }) => theme.neutralText};

        &:first-child {
          width: 25%;
          padding-top: 0;
          padding-bottom: 0;

          /* The page's generic "span" rule would grey the specimen and add a margin. */
          span {
            color: inherit;
            margin-right: 0;
          }
        }

        &:not(:first-child) {
          ${textStyle('h5')}
        }
      }
    }

    /* The Updated frame sets the family column of the font sizes table in Neutral/text-weak, the other values in Neutral/text. */
    &-font td:nth-child(2) {
      color: ${({ theme }) => theme.neutralTextWeak};
    }

    &-font-2 th {
      height: 1.5rem;
    }
  }
`;

export default Typography;
